<template>
  <div class="flex flex-col items-center justify-center gap-[32px] md:gap-1">
    <h3
      class="mb-0 md:mb-[32.25px] mt-0 text-[20px] font-ff-good-pro-cond font-medium !leading-[26px] md:text-[24px] md:!leading-[34px] text-center text-black"
    >
      {{ $t("common.choose_your_inner_leg_length") }}
    </h3>
    <div class="grid grid-cols-1 md:grid-cols-10 gap-[30px] md:gap-[65px]">
      <div class="flex justify-center md:justify-end md:col-span-4">
        <ManMap class="w-[211px]" v-if="gender == 'male'" />
        <WomanMap class="w-[211px]" v-else />
      </div>
      <div class="md:col-span-4">
        <span
          class="md:mt-[18px] flex items-center gap-1 text-sm font-bold uppercase text-secondary font-ff-good-pro-cond"
        >
          <Tips class="w-[18px] h-[18px]" />
          {{ $t("common.measuring_tips") }}
        </span>
        <ol
          class="pl-5 mt-3 text-sm !leading-5 list-decimal font-ff-good-pro-cond space-y-1.5 text-black"
        >
          <li>
            {{ $t("common.inner_leg_measuring_tips_list_item_1") }}
          </li>
          <li>
            {{ $t("common.inner_leg_measuring_tips_list_item_2") }}
          </li>
          <li>
            {{ $t("common.inner_leg_measuring_tips_list_item_3") }}
          </li>
        </ol>
        <p class="mt-[22px] text-black text-sm font-ff-good-pro-cond">
          {{ $t("common.inner_leg_length_measurement_notes") }}
        </p>
        <div class="mt-[42px]">
          <label
            for="innerLegLength"
            class="block text-sm font-bold leading-6 text-secondary uppercase font-ff-good-pro-cond tracking-[0.14px]"
            v-text="$t('common.inner_leg_length')"
          />
          <div class="flex items-center gap-2 mt-1">
            <input
              id="innerLegLength"
              :placeholder="$t('common.placeholder')"
              v-model="innerLegLength"
              type="number"
              name="innerLegLength"
              autocomplete="innerLegLength"
              class="block w-full placeholder:text-lg px-4 py-3 max-w-[300px] rounded-md bg-[#FFFFFF] font-normal border-light-gray-200 text-lg leading-6 placeholder:text-[#949494] font-ff-good-pro-cond bg-opacity-[0.06] shadow-sm text-black focus:border-primary focus:ring-0"
              :class="{
                'is-invalid':
                  !!errorsMessage ||
                  (calculateBikeSizeStore.errors &&
                    calculateBikeSizeStore.errors.inner_leg_length),
              }"
              @input="clearValidation"
            />
            <div
              class="flex items-center gap-2 text-[#747474] text-base w-[54px]"
            >
              <span
                :class="unit == 'cm' ? 'text-primary font-semibold' : ''"
                class="cursor-pointer"
                @click="() => setUnit('cm')"
              >
                cm
              </span>
              <span class="bg-light-gray-200 h-[30px] w-[1px]"></span>
              <span
                :class="unit == 'in' ? 'text-primary font-semibold' : ''"
                class="cursor-pointer"
                @click="() => setUnit('in')"
              >
                in
              </span>
            </div>
          </div>
          <p
            v-if="
              errorsMessage ||
              (calculateBikeSizeStore.errors &&
                calculateBikeSizeStore.errors.inner_leg_length)
            "
            class="mt-1 text-sm text-red-400"
            v-text="
              errorsMessage || calculateBikeSizeStore.errors.inner_leg_length[0]
            "
          />
        </div>
      </div>
    </div>
    <div class="flex w-full gap-2 md:w-auto">
      <Btn
        type="button"
        size="lg"
        block
        :rounded="false"
        class="w-full md:!w-auto uppercase rounded-md px-[30px]"
        color="secondary"
        v-text="$t('common.back')"
        @click="goBack"
      />
      <Btn
        type="button"
        size="lg"
        :disabled="calculateBikeSizeStore.isLoading"
        block
        :rounded="false"
        class="w-full md:!w-auto uppercase rounded-md px-[30px]"
        color="primary"
        @click="goNext"
      >
        <div class="flex items-center justify-center space-x-2">
          <Spinner
            v-if="calculateBikeSizeStore.isLoading"
            class="text-white animate-spin"
          />
          <span
            class="text-lg font-semibold font-ff-good-pro-cond text-white leading-[26px] uppercase"
          >
            {{ $t("common.apply") }}
          </span>
        </div>
      </Btn>
    </div>
  </div>
</template>
<script setup>
import Tips from "~/assets/svg/heroicons/tips.svg?component";
import ManMap from "~/assets/svg/calculate-size/man-inner-leg-map.svg?component";
import WomanMap from "~/assets/svg/calculate-size/woman-inner-leg-map.svg?component";
import { useCalculateBikeSizeStore } from "~/store/calculatebikesize";
import { useTranslation } from "i18next-vue";

const calculateBikeSizeStore = useCalculateBikeSizeStore();
const { t } = useTranslation();
const emit = defineEmits();

const gender = ref(calculateBikeSizeStore.form.gender);
const errorsMessage = ref(null);
const innerLegLength = ref(calculateBikeSizeStore.form.inner_leg_length);
const unit = ref(calculateBikeSizeStore.form.unit);

function clearValidation() {
  errorsMessage.value = null;
  calculateBikeSizeStore.clearValidation("inner_leg_length");
}

function goNext() {
  if (!innerLegLength.value) {
    errorsMessage.value = t("common.field_required", {
      attribute: t("common.inner_leg_length"),
      interpolation: { prefix: "[:", suffix: "]" },
    });
    return false;
  }

  calculateBikeSizeStore.updateForm("inner_leg_length", innerLegLength.value);

  const callback = () => {
    const timeout = setTimeout(() => {
      const containerEl = document.getElementById("calculate");
      if (containerEl) {
        containerEl.scrollIntoView({
          behavior: "smooth",
        });
      }
      clearTimeout(timeout);
    }, 5);
  };
  calculateBikeSizeStore.fetchResults(callback);
}
function goBack() {
  emit("back", "height");
  if (innerLegLength.value) {
    calculateBikeSizeStore.updateForm("inner_leg_length", innerLegLength.value);
  }
}
function setUnit(ut) {
  if (calculateBikeSizeStore.form.unit != ut) {
    const height = calculateBikeSizeStore.form.height;
    if (height && ut == "cm") {
      calculateBikeSizeStore.updateForm(
        "height",
        parseFloat(height * 2.54).toFixed(2)
      );
    } else if (height && ut == "in") {
      calculateBikeSizeStore.updateForm(
        "height",
        parseFloat(height / 2.54).toFixed(2)
      );
    }
  }
  calculateBikeSizeStore.updateForm("unit", ut);
  unit.value = ut;
}
</script>